/**
 * THE TRIAL CONVERSION VOCABULARY (Miles, 2026-10-20): a card at trial start, and the first Prest0n when the trial
 * ends. Design record: docs/PLAN-trial-card-on-file-20261020.md.
 *
 * VERBATIM TWINS: functions/src/billing/trialConversionVocabulary.ts and src/types/trialConversion.ts are
 * BYTE-IDENTICAL, pinned by tests/billing/trialConversionVocabulary.parity.test.ts. One owner per vocabulary: the
 * server writes these words and the trial page reads them; neither side keeps a list of its own.
 *
 * Codes, numbers and shapes only. Pure and dependency-free (no `satisfies`: the page's build is CRA's Babel).
 */

/**
 * The disclosed set a person agrees to on the trial screen, beside the automatic-renewal terms version. Bumped when
 * what the screen DISCLOSES changes (a new fact, or one removed), never for a copy edit: it rides the retained
 * acknowledgment so a later reader can tell which facts this person was shown.
 */
export const TRIAL_CONVERSION_TERMS_VERSION = '2026-10-20.1';

/**
 * The one plan two ticks can start (Miles, 2026-10-20): one Prest0n, monthly, cancel anytime. A team, an annual plan
 * or any later change is a signed Order. The price is the catalog's, read by the server at every step.
 */
export const TRIAL_CONVERSION_TIER = 'wu_monthly';
export const TRIAL_CONVERSION_SEATS = 1;

/**
 * A conversion's life.
 *   awaiting_card  a trial that needs a card before any work (stamped when the trial starts)
 *   armed          card and consent on file: the plan starts when the trial ends
 *   off            the person said "don't start my plan": the trial runs to its end, nothing is charged
 *   charging       the sweep holds it while it talks to Stripe
 *   converted      the first month is paid; the plan is live
 *   declined       the first charge failed; it can still be paid on the page until Stripe's window closes
 *   needs_consent  the price, the card or who may pay changed: nothing is charged until someone confirms again
 *   lapsed         the trial ended with nothing to charge (no card, turned off, a declined charge never paid, a
 *                  warning that could not be delivered, a signed hire still going through, or charging held off)
 *   superseded     a signed hire started the plan first
 */
export const TRIAL_CONVERSION_STATES = [
  'awaiting_card', 'armed', 'off', 'charging', 'converted', 'declined', 'needs_consent', 'lapsed', 'superseded',
] as const;
export type TrialConversionState = (typeof TRIAL_CONVERSION_STATES)[number];

export const TRIAL_CONVERSION_TERMINAL_STATES = ['converted', 'lapsed', 'superseded'] as const;

/** Every move a conversion may make; anything else is refused. armed → armed is a new card. */
export const TRIAL_CONVERSION_TRANSITIONS: Readonly<Record<TrialConversionState, readonly TrialConversionState[]>> = {
  awaiting_card: ['armed', 'lapsed', 'superseded'],
  armed: ['armed', 'off', 'charging', 'needs_consent', 'lapsed', 'superseded'],
  off: ['armed', 'lapsed', 'superseded'],
  charging: ['converted', 'declined', 'armed', 'needs_consent', 'superseded'],
  declined: ['converted', 'lapsed', 'superseded'],
  needs_consent: ['armed', 'off', 'lapsed', 'superseded'],
  converted: [],
  lapsed: [],
  superseded: [],
};

export function canTrialConversionMove(from: TrialConversionState, to: TrialConversionState): boolean {
  return (TRIAL_CONVERSION_TRANSITIONS[from] || []).indexOf(to) >= 0;
}

/** The only state that refuses work: a trial that needs a card and has none yet. "off" never ends a trial early. */
export const TRIAL_CONVERSION_BLOCKING_STATES = ['awaiting_card'] as const;

/**
 * The plan starts AFTER the trial (the sweep runs every ten minutes), so an armed trial keeps working past its end
 * until its first charge resolves — at most this long. A decline pauses work at once (no grace).
 */
export const TRIAL_CONVERSION_CHARGE_WINDOW_STATES = ['armed', 'charging'] as const;

/** Why a conversion waits for someone to confirm again. */
export const TRIAL_CONVERSION_CONSENT_REASONS = ['price-changed', 'terms-changed', 'card-removed', 'card-expired', 'authority-lost'] as const;
export type TrialConversionConsentReason = (typeof TRIAL_CONVERSION_CONSENT_REASONS)[number];

/**
 * What the platform tells a person, and when (D18: notices at set times before a charge the person agreed to).
 *   armed               the acknowledgment: the terms accepted, the date, the amount, the card and how to cancel
 *   reminder_7d/3d/1d   before the trial ends (the 7-day one only for trials longer than 7 days)
 *   converted           the receipt
 *   declined            the first charge failed; one tap to fix
 *   declined_last_call  still unpaid, before Stripe's window closes
 *   turned_off          "you won't be charged"
 *   needs_consent       nothing will be charged until someone confirms again
 *   invite_card         a trial running without a card: add one to keep Prest0n working (never required)
 */
export const TRIAL_CONVERSION_NOTICE_KINDS = [
  'armed', 'reminder_7d', 'reminder_3d', 'reminder_1d', 'converted', 'declined', 'declined_last_call', 'turned_off',
  'needs_consent', 'invite_card',
] as const;
export type TrialConversionNoticeKind = (typeof TRIAL_CONVERSION_NOTICE_KINDS)[number];

/** The notices that also go by text, where the person confirmed a number. Email and the bell carry every one. */
export const TRIAL_CONVERSION_TEXTED_KINDS = ['reminder_3d', 'reminder_1d', 'converted', 'declined', 'declined_last_call'] as const;

export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;

/**
 * When each reminder's window opens, before the plan starts; it runs to the next closer one's. Reminders wait for the
 * person's daytime inside their window, so the "7-day" one opens a day early: it still lands at least 7 days ahead.
 * Every reminder names the date, never "in N days".
 */
export const TRIAL_REMINDER_LEADS_MS: Readonly<Record<'reminder_7d' | 'reminder_3d' | 'reminder_1d', number>> = {
  reminder_7d: 8 * DAY_MS,
  reminder_3d: 3 * DAY_MS,
  reminder_1d: 1 * DAY_MS,
};

/** No warning, no charge: the charge waits at least this long after its warning actually went out (Visa). */
export const TRIAL_WARNING_LEAD_MS = 7 * DAY_MS;

/**
 * A conversion armed this close to its charge (or closer) is warned by its acknowledgment, which already says when and
 * how much; one armed earlier is warned by the 7-day reminder (Visa: trials longer than 7 days get a reminder).
 */
export const SHORT_TRIAL_MAX_MS = 8 * DAY_MS;

/** How long an ended, armed trial keeps working while its first charge resolves. */
export const TRIAL_CONVERSION_CHARGE_WINDOW_MS = 2 * HOUR_MS;

/** Stripe keeps an unpaid first invoice payable for 23 hours; the last call goes before that. */
export const DECLINED_PAY_WINDOW_MS = 23 * HOUR_MS;
export const DECLINED_LAST_CALL_AFTER_MS = 20 * HOUR_MS;

/** A trial running without a card is invited to add one this long before it ends (once). */
export const INVITE_CARD_LEAD_MS = 3 * DAY_MS;

/** Texts land only in these local hours [start, end), in the time zone recorded when the card was added. */
export const TEXT_HOURS_START = 9;
export const TEXT_HOURS_END = 20;

/** What an endpoint answers when it refuses; the server sends the words, the page keeps a fallback per code. */
export const TRIAL_CONVERSION_REFUSALS = [
  'not-signed-in', 'not-allowed', 'not-offered', 'no-trial', 'trial-ended', 'plan-exists', 'hire-pending',
  'granted-plan', 'exempt', 'setup-incomplete', 'card-wrong-customer', 'card-expired', 'consent-absent',
  'consent-malformed', 'consent-stale-version', 'consent-mismatch', 'terms-not-accepted', 'not-armed', 'charging',
  'nothing-to-pay', 'pay-window-closed', 'card-declined', 'unavailable',
] as const;
export type TrialConversionRefusal = (typeof TRIAL_CONVERSION_REFUSALS)[number];

/** The card a conversion charges, as the page draws it. Never the payment method's id. */
export interface TrialConversionCard {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/** What the trial page asks the server (trialConversionStatus) before it draws anything. */
export interface TrialConversionStatus {
  accountId: string;
  /** The lever offers the screen to this account. */
  offered: boolean;
  /** This trial must have a card before any work. */
  required: boolean;
  /** A trial this caller starts now would need a card first: onboarding shows its card step on this answer. */
  startRequiresCard: boolean;
  /** null: no conversion record (a trial from before cards, or no trial at all). */
  state: TrialConversionState | null;
  trialActive: boolean;
  trialEndsAtMs: number | null;
  trialDays: number | null;
  trialCreditCents: number | null;
  /** When the plan starts: the trial's end, or later when a warning went out late. */
  chargeAtMs: number | null;
  /** One Prest0n, one month, in cents: the catalog's price. */
  amountCents: number;
  /** pr0 a month the plan includes. */
  allocationPr0: number;
  tier: string;
  seats: number;
  /** How often the plan charges, and its binding term in months (0: none) — echoed back in the consent. */
  interval: 'month' | 'year';
  commitmentMonths: number;
  card: TrialConversionCard | null;
  /** The versions the page must send back with its consent. */
  renewalTermsVersion: string;
  trialTermsVersion: string;
  /** A declined first charge that can still be paid on the page, until when. */
  payableUntilMs: number | null;
  consentReason: TrialConversionConsentReason | null;
  /** The caller may add or change the card, or turn the plan's start off and on. */
  canManage: boolean;
  /**
   * The card to offer where the page asks for one (never while armed: a change is a new card): the conversion's own
   * while it stands, else the billing profile's — never one that will have expired when the plan starts.
   */
  savedCard: TrialConversionSavedCard | null;
}

/** A card already on the billing profile, offered so the person need not type it again. */
export interface TrialConversionSavedCard extends TrialConversionCard {
  paymentMethodId: string;
}

/** What the page sends when the person ticks both boxes and presses start. */
export interface TrialArmRequest {
  accountId: string;
  /** A card just entered (a confirmed SetupIntent on the billing profile), or one already on it. */
  setupIntentId?: string;
  paymentMethodId?: string;
  /** The automatic-renewal consent, in the shape billing/autoRenewalDisclosure.ts verifies. */
  autoRenewalConsent: {
    tier: string;
    seats: number;
    perSeatCents: number;
    interval: 'month' | 'year';
    commitmentMonths: number;
    acceptedAt: string;
    termsVersion: string;
  };
  trialTermsVersion: string;
  /** The Terms and Privacy box: the versions the page showed. */
  terms: { tosVersion: string; privacyVersion: string; acceptedAtMs: number };
  /** The person's IANA time zone, so dates in emails and the hours of texts are theirs. */
  timeZone: string | null;
}
